/**
 * XML Parser utility for NaraSpeller API responses
 */

/**
 * Parse XML string using browser's DOMParser
 */
export function parseXML(xmlString: string): Document {
  const parser = new DOMParser()
  const doc = parser.parseFromString(xmlString, 'text/xml')
  
  // Check for parsing errors
  const parserError = doc.querySelector('parsererror')
  if (parserError) {
    throw new Error(`XML 파싱 실패: ${parserError.textContent}`)
  }
  
  return doc
}

/**
 * Get text content from XML element safely
 */
export function getTextContent(element: Element | null, tagName: string): string {
  if (!element) return ''
  const child = element.querySelector(tagName)
  return child?.textContent?.trim() || ''
}

/**
 * Get number content from XML element safely
 */
export function getNumberContent(element: Element | null, tagName: string, defaultValue = 0): number {
  const text = getTextContent(element, tagName)
  const num = parseInt(text, 10)
  return isNaN(num) ? defaultValue : num
}

/**
 * Get all elements matching a selector
 */
export function getAllElements(doc: Document | Element, selector: string): Element[] {
  return Array.from(doc.querySelectorAll(selector))
}

/**
 * NaraSpeller specific XML structure
 */
export interface NaraSpellerError {
  str: string           // Text segment
  help: string          // Error description
  errorIdx: number      // Error index
  correctMethod: number // Correction method (0-10)
  start: number        // Start position
  end: number          // End position
  orgStr: string       // Original text
  candWord: string     // Correction suggestion
}

/**
 * Parse NaraSpeller XML response
 */
export function parseNaraSpellerResponse(xmlString: string): NaraSpellerError[] {
  const doc = parseXML(xmlString)
  const errors: NaraSpellerError[] = []
  
  // Get all result elements
  const results = getAllElements(doc, 'result')
  
  for (const result of results) {
    // Check if this result has an error
    const errInfo = result.querySelector('errInfo')
    
    if (errInfo) {
      const error: NaraSpellerError = {
        str: getTextContent(result, 'str'),
        help: getTextContent(errInfo, 'help'),
        errorIdx: getNumberContent(errInfo, 'errorIdx'),
        correctMethod: getNumberContent(errInfo, 'correctMethod'),
        start: getNumberContent(errInfo, 'start'),
        end: getNumberContent(errInfo, 'end'),
        orgStr: getTextContent(errInfo, 'orgStr'),
        candWord: getTextContent(errInfo, 'candWord')
      }
      
      errors.push(error)
    }
  }
  
  return errors
}

/**
 * Map correctMethod to error level
 */
export function mapCorrectMethodToLevel(correctMethod: number): 'error' | 'warning' | 'suggestion' {
  switch(correctMethod) {
    case 0: // 띄어쓰기
      return 'warning'
    case 1: // 맞춤법
      return 'error'
    case 2: // 표준어
      return 'error'
    case 3: // 통사
      return 'warning'
    case 4: // 기타
      return 'suggestion'
    default:
      return correctMethod <= 2 ? 'error' : 'suggestion'
  }
}

/**
 * Map correctMethod to error type
 */
export function mapCorrectMethodToType(correctMethod: number): 'spelling' | 'grammar' | 'punctuation' | 'style' | 'typo' {
  const typeMap: Record<number, 'spelling' | 'grammar' | 'punctuation' | 'style' | 'typo'> = {
    0: 'spelling',      // 띄어쓰기 -> spelling
    1: 'spelling',     // 맞춤법
    2: 'spelling',     // 표준어 -> spelling
    3: 'grammar',      // 통사 -> grammar
    4: 'style',        // 기타 -> style
    5: 'style',        // 단어 선택 -> style
    6: 'punctuation',  // 문장부호
    7: 'grammar',      // 문법
    8: 'style',        // 문체
    9: 'style',        // 존댓말 -> style
    10: 'style'        // 표현 -> style
  }
  
  return typeMap[correctMethod] || 'spelling'
}

/**
 * Get error description based on correctMethod
 */
export function getErrorDescription(correctMethod: number, help?: string): string {
  if (help && help.trim()) {
    return help
  }
  
  const descriptions: Record<number, string> = {
    0: '띄어쓰기 오류입니다',
    1: '맞춤법 오류입니다',
    2: '표준어 오류입니다',
    3: '문법 오류입니다',
    4: '기타 오류입니다',
    5: '단어 선택 오류입니다',
    6: '문장부호 오류입니다',
    7: '문법 오류입니다',
    8: '문체 개선이 필요합니다',
    9: '존댓말 사용 오류입니다',
    10: '표현 개선이 필요합니다'
  }
  
  return descriptions[correctMethod] || '맞춤법 검사 오류'
}